import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { WORK as DEFAULT_WORK } from "../data/content";
import { useContent } from "../data/ContentContext";

// Work row with always-visible media thumbnail + on-hover video preview (muted, loops).
const Row = ({ project, onOpen }) => {
  const videoRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  const enter = () => {
    setHovered(true);
    if (videoRef.current && project.previewVideo) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };
  const leave = () => {
    setHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={leave}
      onClick={() => onOpen?.(project)}
      className="work-row group w-full grid grid-cols-12 gap-4 md:gap-6 items-center py-6 md:py-8 text-left"
      data-testid={`work-row-${project.id}`}
    >
      <div className="col-span-2 md:col-span-1 overline self-start pt-2">{project.index}</div>

      <div className="col-span-10 md:col-span-5 lg:col-span-5 work-title">
        <h3 className="editorial-h2 text-3xl md:text-5xl lg:text-6xl leading-[0.95]">
          {project.title}
        </h3>
        <div className="text-sm md:text-base mt-2 text-black/65 font-medium">
          {project.subtitle}
        </div>
        <p className="hidden md:block text-sm text-black/55 mt-3 max-w-md leading-relaxed">
          {project.preview}
        </p>
      </div>

      {/* Inline media preview: poster image, plays muted video on hover */}
      <div className="col-span-12 md:col-span-5 lg:col-span-5 relative">
        <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden bg-black/5 border border-black/10">
          <img
            src={project.previewPoster}
            alt={project.title}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              hovered && project.previewVideo ? "opacity-0" : "opacity-100"
            } group-hover:scale-[1.03] transition-transform duration-1000`}
            data-testid={`work-preview-image-${project.id}`}
          />
          {project.previewVideo && (
            <video
              ref={videoRef}
              src={project.previewVideo}
              muted
              loop
              playsInline
              preload="metadata"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
                hovered ? "opacity-100" : "opacity-0"
              }`}
              data-testid={`work-preview-video-${project.id}`}
            />
          )}
          {/* Subtle ribbon on the preview */}
          <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-[var(--bg)] px-2.5 py-1 border border-black/15 text-[10px] uppercase tracking-[0.2em] font-semibold">
            {project.year}
          </div>
          {project.previewVideo && (
            <div
              className={`absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-[var(--ink)] text-[var(--bg)] px-2.5 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold transition-opacity ${
                hovered ? "opacity-0" : "opacity-90"
              }`}
            >
              <Play size={11} /> Preview
            </div>
          )}
        </div>
      </div>

      <div className="hidden md:flex col-span-1 justify-end items-center">
        <ArrowUpRight className="work-arrow" size={28} />
      </div>

      {/* Mobile preview text */}
      <div className="col-span-12 md:hidden mt-1">
        <p className="text-sm text-black/65 leading-relaxed">{project.preview}</p>
      </div>
    </motion.button>
  );
};

const Work = ({ onOpen }) => {
  const { content } = useContent();
  const WORK = content?.work || DEFAULT_WORK;
  return (
    <section id="work" className="relative py-24 md:py-32 px-6 md:px-10 lg:px-14" data-testid="work-section">
      <div className="max-w-[1440px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-12 gap-6 mb-14 md:mb-20"
        >
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-4">— Selected Work</div>
            <h2 className="editorial-h2 text-5xl md:text-7xl">Project highlights.</h2>
          </div>
        </motion.div>

        <div className="relative">
          {WORK.map((p) => (
            <Row key={p.id} project={p} onOpen={onOpen} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Work;
